import { useId } from 'react';
import { Button } from '../../components/Button';
import { ItemCard } from '../../components/ItemCard';
import { TextButton } from '../../components/TextButton';
import { CardWrapper, QuantityText, SelectedInfo } from './ExtrasCard.styles';
import type { ExtrasCardProps } from './ExtrasCard.types';

export function ExtrasCard({
  image,
  imageAspectRatio,
  imageMobileAspectRatio,
  title,
  description,
  variant = 'default',
  price,
  selected = false,
  quantity,
  availability = 'available',
  availabilityLabel,
  onAdd,
  onRemove,
  onEditSelections,
  labels = {},
}: ExtrasCardProps) {
  const {
    add = 'Add',
    remove = 'Remove',
    quantity: quantityLabel = 'Quantity',
    editSelections = 'Edit selections',
    noImage = 'No image',
  } = labels;

  const stacked = variant === 'stacked';
  const stockLabelId = useId();
  const dimmed = availability === 'unavailable' && !selected;
  const stockLabel = availability === 'available' ? undefined : availabilityLabel;
  const stockBadge =
    stockLabel && !dimmed ? <span id={stockLabelId}>{stockLabel}</span> : undefined;

  const customAction = (
    <Button
      type="button"
      variant={selected ? 'outline' : 'primary'}
      fullWidth={stacked || undefined}
      aria-label={`${selected ? remove : add}, ${title}`}
      disabled={dimmed}
      aria-describedby={stockLabel ? stockLabelId : undefined}
      onClick={selected ? onRemove : onAdd}
    >
      {selected ? remove : add}
    </Button>
  );

  const selectedInfo =
    selected && (quantity || onEditSelections) ? (
      <SelectedInfo>
        {!!quantity && <QuantityText>{`${quantityLabel} ${quantity}`}</QuantityText>}
        {onEditSelections && (
          <TextButton
            size="base"
            aria-label={`${editSelections}, ${title}`}
            onClick={onEditSelections}
          >
            {editSelections}
          </TextButton>
        )}
      </SelectedInfo>
    ) : undefined;

  const meta =
    description && selectedInfo ? (
      <SelectedInfo>
        {description}
        {selectedInfo}
      </SelectedInfo>
    ) : (
      description || selectedInfo
    );

  return (
    <CardWrapper>
      <ItemCard
        variant={stacked ? 'stacked' : undefined}
        image={{ ...image, placeholderLabel: image.placeholderLabel ?? noImage }}
        imageAspectRatio={imageAspectRatio}
        imageMobileAspectRatio={imageMobileAspectRatio}
        title={title}
        price={dimmed ? undefined : price}
        imageBadge={stockBadge}
        unavailable={dimmed}
        unavailableLabel={dimmed ? stockLabel : undefined}
        unavailableLabelId={stockLabelId}
        customAction={customAction}
        meta={meta}
      />
    </CardWrapper>
  );
}
